// PlantsonasTab.jsx — grid + editor for the species-level "plantsona"
// drafts that ship with 2.9. Each Species doc carries a self-contained
// `persona` object (kept self-contained so it lifts cleanly onto LeafKeys in
// 3.x):
//   persona: {
//     archetype, voice, traits[], loves[], hates[], promptBlock, timber,
//     status: "draft" | "live", updatedAt, updatedBy
//   }

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  deleteField,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import db, { auth } from "../../Firebase";
import { getImageURLFromDatabase } from "../../DatabaseOperations";

const STATUS_META = {
  live: { label: "Live", className: "cv2ChipLive" },
  draft: { label: "Draft", className: "cv2ChipDraft" },
  none: { label: "Missing", className: "cv2ChipNone" },
};

const personaStatus = (species) => {
  if (!species.persona) return "none";
  return species.persona.status === "live" ? "live" : "draft";
};

const hasArt = (species) => {
  const stages = species.stages;
  const list = Array.isArray(stages) ? stages : Object.values(stages || {});
  return list.some((s) => s?.plantBody?.imageName);
};

const bestArtImageName = (species) => {
  const stages = species.stages;
  const list = Array.isArray(stages) ? stages : Object.values(stages || {});
  const withArt = list.filter((s) => s?.plantBody?.imageName);
  return withArt.length > 0 ? withArt[withArt.length - 1].plantBody.imageName : null;
};

const SpeciesThumb = ({ species }) => {
  const [url, setUrl] = useState("");
  const imageName = bestArtImageName(species);
  useEffect(() => {
    let mounted = true;
    if (!imageName) return undefined;
    getImageURLFromDatabase("spriteComponents/plants/" + imageName).then(
      (u) => mounted && setUrl(u),
      () => {}
    );
    return () => {
      mounted = false;
    };
  }, [imageName]);
  if (!imageName) return <div className="cv2Thumb cv2ThumbEmpty">🌱</div>;
  return (
    <div className="cv2Thumb">
      {url ? <img src={url} alt={species.id} /> : null}
    </div>
  );
};

const listToText = (list) => (Array.isArray(list) ? list.join(", ") : "");
const textToList = (text) =>
  text
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);

const PersonaEditor = ({ species, onClose, onSaved }) => {
  const persona = species.persona || {};
  const [form, setForm] = useState({
    archetype: persona.archetype || "",
    voice: persona.voice || "",
    traits: listToText(persona.traits),
    loves: listToText(persona.loves),
    hates: listToText(persona.hates),
    promptBlock: persona.promptBlock || "",
    timber: persona.timber || "",
    status: persona.status === "live" ? "live" : "draft",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const newPersona = {
        archetype: form.archetype.trim(),
        voice: form.voice.trim(),
        traits: textToList(form.traits),
        loves: textToList(form.loves),
        hates: textToList(form.hates),
        promptBlock: form.promptBlock.trim(),
        timber: form.timber.trim(),
        status: form.status,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email || auth.currentUser?.uid || "",
      };
      await updateDoc(doc(db, "Species", species.id), { persona: newPersona });
      onSaved({ ...species, persona: { ...newPersona, updatedAt: new Date() } });
    } catch (err) {
      console.error("Persona save failed", err);
      setError(err?.message || "Save failed");
      setSaving(false);
    }
  };

  const removePersona = async () => {
    if (!window.confirm(`Delete the persona on ${species.id}?`)) return;
    setSaving(true);
    setError("");
    try {
      await updateDoc(doc(db, "Species", species.id), {
        persona: deleteField(),
      });
      onSaved({ ...species, persona: undefined });
    } catch (err) {
      console.error("Persona delete failed", err);
      setError(err?.message || "Delete failed");
      setSaving(false);
    }
  };

  return (
    <div className="cv2ModalBackdrop" onClick={onClose}>
      <div className="cv2Modal" onClick={(e) => e.stopPropagation()}>
        <div className="cv2ModalHeader">
          <div>
            <h3>{species.id}</h3>
            {species.commonName ? (
              <div className="cv2ModalSub">{species.commonName}</div>
            ) : null}
          </div>
          <button className="cv2IconButton" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="cv2FormGrid">
          <label>
            Archetype
            <input
              value={form.archetype}
              onChange={set("archetype")}
              placeholder='e.g. "The Diva", "Ol&apos; Reliable"'
            />
          </label>
          <label>
            Status
            <select value={form.status} onChange={set("status")}>
              <option value="draft">Draft (impending)</option>
              <option value="live">Live</option>
            </select>
          </label>
          <label className="cv2Span2">
            Voice
            <input
              value={form.voice}
              onChange={set("voice")}
              placeholder="How they talk — cadence, vocabulary, attitude"
            />
          </label>
          <label className="cv2Span2">
            Traits <span className="cv2Hint">comma-separated</span>
            <textarea rows={2} value={form.traits} onChange={set("traits")} />
          </label>
          <label>
            Loves <span className="cv2Hint">comma-separated</span>
            <textarea rows={2} value={form.loves} onChange={set("loves")} />
          </label>
          <label>
            Hates <span className="cv2Hint">comma-separated</span>
            <textarea rows={2} value={form.hates} onChange={set("hates")} />
          </label>
          <label className="cv2Span2">
            Prompt block{" "}
            <span className="cv2Hint">injected into the chat system prompt</span>
            <textarea
              rows={5}
              value={form.promptBlock}
              onChange={set("promptBlock")}
            />
          </label>
          <label className="cv2Span2">
            Timber <span className="cv2Hint">how Timber introduces this species</span>
            <textarea rows={3} value={form.timber} onChange={set("timber")} />
          </label>
        </div>

        {error ? <div className="cv2Error">{error}</div> : null}

        <div className="cv2ModalFooter">
          {species.persona ? (
            <button
              className="cv2Button cv2ButtonDanger"
              onClick={removePersona}
              disabled={saving}
            >
              Delete persona
            </button>
          ) : (
            <span />
          )}
          <div>
            <button className="cv2Button" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button
              className="cv2Button cv2ButtonPrimary"
              onClick={save}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PlantsonasTab = () => {
  const [speciesList, setSpeciesList] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [coreOnly, setCoreOnly] = useState(true);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getDocs(collection(db, "Species"))
      .then((snap) => {
        if (cancelled) return;
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const artDiff = (hasArt(b) ? 1 : 0) - (hasArt(a) ? 1 : 0);
          return artDiff !== 0 ? artDiff : a.id.localeCompare(b.id);
        });
        setSpeciesList(rows);
      })
      .catch((err) => {
        console.error("Species load failed", err);
        if (!cancelled) setError(err?.message || "Failed to load Species");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!speciesList) return [];
    const term = search.trim().toLowerCase();
    return speciesList.filter((s) => {
      if (coreOnly && !hasArt(s)) return false;
      if (statusFilter !== "all" && personaStatus(s) !== statusFilter)
        return false;
      if (
        term &&
        !s.id.toLowerCase().includes(term) &&
        !(s.commonName || "").toLowerCase().includes(term)
      )
        return false;
      return true;
    });
  }, [speciesList, search, statusFilter, coreOnly]);

  const counts = useMemo(() => {
    const base = { live: 0, draft: 0, none: 0 };
    (speciesList || []).filter((s) => !coreOnly || hasArt(s)).forEach((s) => {
      base[personaStatus(s)] += 1;
    });
    return base;
  }, [speciesList, coreOnly]);

  if (error) return <div className="cv2Error">{error}</div>;
  if (!speciesList) return <div className="cv2Loading">Loading species…</div>;

  return (
    <div>
      <div className="cv2Toolbar">
        <input
          className="cv2Search"
          placeholder="Search species…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All statuses</option>
          <option value="none">Missing</option>
          <option value="draft">Draft</option>
          <option value="live">Live</option>
        </select>
        <label className="cv2Check">
          <input
            type="checkbox"
            checked={coreOnly}
            onChange={(e) => setCoreOnly(e.target.checked)}
          />
          Species with art only
        </label>
        <div className="cv2CountStrip">
          {counts.live} live · {counts.draft} draft · {counts.none} missing
        </div>
      </div>

      <div className="cv2SpeciesGrid">
        {filtered.map((s) => {
          const status = personaStatus(s);
          const meta = STATUS_META[status];
          return (
            <button
              className="cv2SpeciesCard"
              key={s.id}
              onClick={() => setEditing(s)}
            >
              <SpeciesThumb species={s} />
              <div className="cv2SpeciesInfo">
                <div className="cv2SpeciesName">{s.id}</div>
                {s.commonName ? (
                  <div className="cv2SpeciesCommon">{s.commonName}</div>
                ) : null}
                <div className="cv2SpeciesMeta">
                  <span className={`cv2Chip ${meta.className}`}>
                    {meta.label}
                  </span>
                  {s.persona?.archetype ? (
                    <span className="cv2Archetype">{s.persona.archetype}</span>
                  ) : null}
                </div>
              </div>
            </button>
          );
        })}
        {filtered.length === 0 ? (
          <div className="cv2Loading">No species match.</div>
        ) : null}
      </div>

      {editing ? (
        <PersonaEditor
          species={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setSpeciesList((list) =>
              list.map((s) => (s.id === updated.id ? updated : s))
            );
            setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
};

export default PlantsonasTab;
